import { ExternalLinkIcon } from 'lucide-react';
import { ISchedule } from '@gettactic/api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@v2/components/ui/tooltip';

// Base interface for props that are always passed to the wrapper
interface JoinButtonWrapperProps {
  onClick: (e: React.MouseEvent<any>) => void;
  className?: string;
  disabled?: boolean;
}

// Generic type for wrapper functions that can accept different prop types
type WrapperFunction<TProps extends JoinButtonWrapperProps = JoinButtonWrapperProps> = (
  children: React.ReactNode | undefined,
  icon: React.ReactNode,
  props: TProps
) => React.ReactNode;

// Base props interface for the JoinButton
interface JoinButtonBaseProps {
  onlineMeeting?: ISchedule['online_meeting'] | null;
  iconOnly?: boolean;
  onRefetch?: () => void;
}

// Generic JoinButton component that can work with different wrapper types
export const JoinButton = <TProps extends JoinButtonWrapperProps = JoinButtonWrapperProps>({
  onlineMeeting,
  iconOnly = false,
  onRefetch,
  wrapper
}: JoinButtonBaseProps & {
  wrapper: WrapperFunction<TProps>;
}) => {
  if (!onlineMeeting) return null;

  const isPending = onlineMeeting.status === 'pending';
  const isDisabled = isPending;

  const onClick = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    
    if (isPending) {
      // Try to refetch data when status is pending
      if (onRefetch) {
        onRefetch();
      }
      return;
    }

    // Only open URL if we have one and status is not pending
    if (onlineMeeting.url && onlineMeeting.status === 'success') {
      window.open(onlineMeeting.url, '_blank', 'noopener,noreferrer');
    }
  };

  const buttonContent = wrapper(
    iconOnly ? (
      <span className="overflow-hidden whitespace-nowrap">
        <span className="pl-2">Join</span>
      </span>
    ) : (
      'Join'
    ),
    <JoinIcon />,
    {
      onClick,
      disabled: isDisabled,
      className: `
        group
        grid items-center gap-0
        grid-cols-[auto_0fr] hover:grid-cols-[auto_1fr]
        transition-[grid-template-columns] duration-300 ease-in-out
        overflow-hidden
        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
      `
    } as TProps
  );

  // If pending, wrap with tooltip
  if (isPending) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            {buttonContent}
          </TooltipTrigger>
          <TooltipContent>
            Link to join will be available later
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonContent;
};

export const JoinIcon = () => {
  return <ExternalLinkIcon />;
};
